// cspell:ignore convs
import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import {
  SUPPORT_TICKET_STATUSES,
  type SupportTicketListItemResponse,
  type SupportTicketStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import ArrowDownIcon from "@redotech/redo-icons/arrow-down.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { GetUser as User } from "@redotech/redo-model/user";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useSupportTickets } from "../../hooks/use-support-ticket-data";
import * as css from "./merge-ticket-modal.module.css";
import {
  MergeTicketRow,
  TicketRow,
  TicketRowPosition,
} from "./merge-ticket-row";

export interface MergeParams {
  selectedSupportTickets: SupportTicketListItemResponse[];
  primaryConversation: SupportTicketListItemResponse;
  resolvedCustomerId: string;
  assigneeId: MongoUserId | null;
  status: SupportTicketStatus;
}

export interface MergeTicketModalProps {
  open: boolean;
  onClose(): void;
  onMerge(params: MergeParams): void;
  currentConversation: SupportTicketListItemResponse;
}

export const MergeTicketModal = memo(function MergeTicketModal({
  open,
  onClose,
  onMerge,
  currentConversation,
}: MergeTicketModalProps) {
  const [step, setStep] = useState<1 | 2>(1);

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<
    Set<SupportTicketListItemResponse["id"]>
  >(new Set());
  const { data: allConversations } = useSupportTickets(0);

  const [selectedSupportTickets, setSelectedSupportTickets] = useState<
    SupportTicketListItemResponse[]
  >([]);
  const team = useRequiredContext(TeamContext);
  const allUsers = useMemo(
    () => team.users.map((u) => u.user as User),
    [team.users],
  );
  const [resolvedCustomerId, setResolvedCustomerId] = useState<string | null>(
    null,
  );
  const [assigneeId, setAssigneeId] = useState<MongoUserId | null>(
    currentConversation.mongoAssigneeId,
  );
  const [assigneeMismatchResolved, setAssigneeMismatchResolved] =
    useState(false);
  const [status, setStatus] = useState<SupportTicketStatus>(
    currentConversation.status,
  );
  const [statusMismatchResolved, setStatusMismatchResolved] = useState(false);

  // Step 1 computed
  const filteredConversations = useMemo(() => {
    const others = allConversations.filter(
      (c) => c.id !== currentConversation.id,
    );
    if (!search.trim()) {
      return others;
    }
    const lower = search.toLowerCase();
    return others.filter((c) =>
      c.customer?.displayName?.toLowerCase().includes(lower),
    );
  }, [allConversations, currentConversation.id, search]);

  const allConvsForMerge = [currentConversation, ...selectedSupportTickets];
  const uniqueCustomerIds = [
    ...new Set(
      allConvsForMerge
        .map((c) => c.mongoCustomerId)
        .filter((id): id is NonNullable<typeof id> => id !== null),
    ),
  ];
  const hasMismatch = uniqueCustomerIds.length > 1;
  const hasAssigneeMismatch =
    new Set(allConvsForMerge.map((c) => c.mongoAssigneeId ?? "")).size > 1;
  const hasStatusMismatch =
    new Set(allConvsForMerge.map((c) => c.status)).size > 1;
  const canMerge =
    (!hasMismatch || resolvedCustomerId !== null) &&
    (!hasAssigneeMismatch || assigneeMismatchResolved) &&
    (!hasStatusMismatch || statusMismatchResolved);

  const customerOptions = useMemo<RedoListItem<string>[]>(
    () =>
      uniqueCustomerIds.map((customerId) => {
        const conv = allConvsForMerge.find(
          (c) => c.mongoCustomerId === customerId,
        );
        return {
          id: customerId,
          value: customerId,
          type: "text",
          text: conv?.customer?.displayName ?? customerId,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uniqueCustomerIds.join(","), currentConversation, selectedSupportTickets],
  );
  const selectedCustomerItem = customerOptions.find(
    (o) => o.id === resolvedCustomerId,
  );

  const assigneeOptions = useMemo<RedoListItem<MongoUserId | null>[]>(
    () => [
      { id: "", value: null, type: "text", text: "Unassigned" },
      ...allUsers.map((user) => ({
        id: user._id,
        value: user._id as MongoUserId,
        type: "text" as const,
        text: user.name,
      })),
    ],
    [allUsers],
  );
  const selectedAssigneeItem = assigneeOptions.find(
    (o) => o.id === (assigneeId ?? ""),
  );

  const statusOptions = useMemo<RedoListItem<SupportTicketStatus>[]>(
    () =>
      SUPPORT_TICKET_STATUSES.filter(
        (s) => s !== "DELETED" && s !== "HIDDEN",
      ).map((s) => ({
        id: s,
        value: s,
        type: "text" as const,
        text: s.charAt(0) + s.slice(1).toLowerCase(),
      })),
    [],
  );
  const selectedStatusItem = statusOptions.find((o) => o.id === status);

  const handleToggle = (id: SupportTicketListItemResponse["id"]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleClose = () => {
    setStep(1);
    setSearch("");
    setSelectedIds(new Set());
    setSelectedSupportTickets([]);
    onClose();
  };

  const handleContinue = () => {
    const selected = allConversations.filter((c) => selectedIds.has(c.id));
    setSelectedSupportTickets(selected);
    const allConvs = [currentConversation, ...selected];
    const uniqueIds = [
      ...new Set(
        allConvs
          .map((c) => c.mongoCustomerId)
          .filter((id): id is NonNullable<typeof id> => id !== null),
      ),
    ];
    const mismatch = uniqueIds.length > 1;
    setResolvedCustomerId(
      mismatch ? null : (currentConversation.mongoCustomerId ?? null),
    );
    setAssigneeId(currentConversation.mongoAssigneeId);
    setAssigneeMismatchResolved(false);
    setStatus(currentConversation.status);
    setStatusMismatchResolved(false);
    setStep(2);
  };

  const handleMerge = () => {
    if (!resolvedCustomerId) {
      return;
    }
    onMerge({
      selectedSupportTickets,
      primaryConversation: currentConversation,
      resolvedCustomerId,
      assigneeId,
      status,
    });
    handleClose();
  };

  const title =
    step === 1
      ? "Select ticket(s) to merge"
      : `Merge ${selectedSupportTickets.length} ticket${selectedSupportTickets.length !== 1 ? "s" : ""}`;

  const primaryButton =
    step === 1
      ? {
          text: "Continue",
          onClick: handleContinue,
          disabled: selectedIds.size === 0,
          size: "md" as const,
        }
      : {
          text: "Merge",
          onClick: handleMerge,
          disabled: !canMerge,
          size: "md" as const,
        };

  const secondaryButton =
    step === 1
      ? { text: "Cancel", onClick: handleClose, size: "md" as const }
      : { text: "Back", onClick: () => setStep(1), size: "md" as const };

  return (
    <RedoModal
      isOpen={open}
      modalSize="md"
      onModalCloseRequested={handleClose}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      title={title}
    >
      {step === 1 ? (
        <Flex dir="column" gap="lg">
          <RedoTextInput
            autoFocus
            fullWidth
            placeholder="Search"
            setValue={setSearch}
            value={search}
          />
          <Flex
            className={css.ticketListScroll as string}
            dir="column"
            gap="md"
            pr="sm"
          >
            {filteredConversations.length === 0 ? (
              <Flex align="center" justify="center" p="xl">
                <Text fontSize="xs" textColor="tertiary">
                  No other conversations found
                </Text>
              </Flex>
            ) : (
              filteredConversations.map((conversation) => (
                <MergeTicketRow
                  conversation={conversation}
                  currentConversation={currentConversation}
                  isSelected={selectedIds.has(conversation.id)}
                  key={conversation.id}
                  onToggle={handleToggle}
                />
              ))
            )}
          </Flex>
        </Flex>
      ) : (
        <Flex dir="column" gap="xl">
          <div className={css.ticketStack as string}>
            <Flex dir="column" gap="none">
              {selectedSupportTickets.map((c, i) => (
                <TicketRow
                  conversation={c}
                  isSuggested={
                    c.mongoCustomerId === currentConversation.mongoCustomerId
                  }
                  key={c.id}
                  position={
                    i === 0 ? TicketRowPosition.First : TicketRowPosition.Middle
                  }
                />
              ))}
              <TicketRow
                conversation={currentConversation}
                position={TicketRowPosition.Last}
              />
            </Flex>
            <div
              className={css.mergingIntoPill as string}
              style={{
                top: `${(selectedSupportTickets.length / (selectedSupportTickets.length + 1)) * 100}%`,
              }}
            >
              <ArrowDownIcon />
              Merging into
            </div>
          </div>

          <RedoSingleSelectDropdownInput
            description={
              hasMismatch && !resolvedCustomerId
                ? "Mismatch found. Please select one."
                : undefined
            }
            label="Customer"
            options={customerOptions}
            optionSelected={({ item }) => setResolvedCustomerId(item.value)}
            placeholder="Select a customer..."
            selectedItem={selectedCustomerItem}
            size="xs"
            state={hasMismatch && !resolvedCustomerId ? "error" : "default"}
          />

          <RedoSingleSelectDropdownInput
            description={
              hasAssigneeMismatch && !assigneeMismatchResolved
                ? "Mismatch found. Please select one."
                : undefined
            }
            label="Assignee"
            options={assigneeOptions}
            optionSelected={({ item }) => {
              setAssigneeId(item.value);
              setAssigneeMismatchResolved(true);
            }}
            placeholder="Select an assignee..."
            selectedItem={
              hasAssigneeMismatch && !assigneeMismatchResolved
                ? undefined
                : selectedAssigneeItem
            }
            size="xs"
            state={
              hasAssigneeMismatch && !assigneeMismatchResolved
                ? "error"
                : "default"
            }
          />

          <RedoSingleSelectDropdownInput
            description={
              hasStatusMismatch && !statusMismatchResolved
                ? "Mismatch found. Please select one."
                : undefined
            }
            label="Status"
            options={statusOptions}
            optionSelected={({ item }) => {
              setStatus(item.value);
              setStatusMismatchResolved(true);
            }}
            placeholder="Select a status..."
            selectedItem={
              hasStatusMismatch && !statusMismatchResolved
                ? undefined
                : selectedStatusItem
            }
            size="xs"
            state={
              hasStatusMismatch && !statusMismatchResolved ? "error" : "default"
            }
          />
        </Flex>
      )}
    </RedoModal>
  );
});
